import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import useModules from "../hooks/useModules";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import DateInput from "./DateInput";

const EMPTY = {
    contract_type: "casual_engagement",
    start_date: "",
    end_date: "",
    job_title: "",
    module_id: "",
    salary_type: "daily",
    basic_salary: "",
    benefits_component: "0",
    payment_frequency: "daily",
    payment_method: "bank_transfer",
    task_or_assignment: "",
    status: "draft",
    signed_document_url: "",
};

export default function ContractFormDialog({
    open,
    onOpenChange,
    employee,
    onSaved,
}) {
    const { modules } = useModules();
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open && employee) {
            setForm({
                ...EMPTY,
                module_id: employee.module_id,
                job_title: employee.job_title,
                contract_type:
                    employee.employment_type === "casual"
                        ? "casual_engagement"
                        : employee.worker_subtype === "manual"
                          ? "smart_contract_manual"
                          : "smart_contract_non_manual",
                salary_type: employee.employment_type === "casual" ? "daily" : "monthly",
                payment_frequency:
                    employee.employment_type === "casual" ? "daily" : "monthly",
            });
        }
    }, [open, employee]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const isCasual = form.contract_type === "casual_engagement";
    const isSmart = form.contract_type.startsWith("smart_contract");

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!employee?.id) return;
        setSubmitting(true);
        const payload = {
            ...form,
            employee_id: employee.id,
            basic_salary: Number(form.basic_salary) || 0,
            benefits_component: Number(form.benefits_component) || 0,
            end_date: form.end_date || null,
            task_or_assignment: form.task_or_assignment?.trim() || null,
            signed_document_url: form.signed_document_url?.trim() || null,
        };
        try {
            await api.post("/contracts", payload);
            toast.success("Contract created");
            onOpenChange(false);
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[90vh] overflow-y-auto"
                data-testid="contract-form-dialog"
            >
                <DialogHeader>
                    <DialogTitle>
                        Add Contract {employee ? `· ${employee.full_name}` : ""}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-4 pt-2">
                    <div className="grid grid-cols-2 gap-3">
                        <FieldRow label="Contract type" required>
                            <SelectField
                                value={form.contract_type}
                                onChange={(v) => set("contract_type", v)}
                                items={[
                                    {
                                        value: "casual_engagement",
                                        label: "Casual Engagement",
                                    },
                                    {
                                        value: "smart_contract_manual",
                                        label: "Smart Contract · Manual",
                                    },
                                    {
                                        value: "smart_contract_non_manual",
                                        label: "Smart Contract · Non-Manual",
                                    },
                                    { value: "renewal", label: "Renewal" },
                                    { value: "termination", label: "Termination" },
                                    { value: "acknowledgment", label: "Acknowledgment" },
                                ]}
                                testid="contract-type"
                            />
                        </FieldRow>
                        <FieldRow label="Status">
                            <SelectField
                                value={form.status}
                                onChange={(v) => set("status", v)}
                                items={[
                                    { value: "draft", label: "Draft" },
                                    { value: "active", label: "Active" },
                                ]}
                                testid="contract-status"
                            />
                        </FieldRow>
                        <FieldRow label="Job title" required>
                            <Input
                                required
                                value={form.job_title}
                                onChange={(e) => set("job_title", e.target.value)}
                                data-testid="contract-job-title"
                            />
                        </FieldRow>
                        <FieldRow label="Module" required>
                            <SelectField
                                value={form.module_id}
                                onChange={(v) => set("module_id", v)}
                                items={modules.map((m) => ({ value: m.id, label: m.name }))}
                                testid="contract-module"
                            />
                        </FieldRow>
                        <FieldRow label="Start date" required>
                            <DateInput
                                required
                                value={form.start_date}
                                onChange={(v) => set("start_date", v)}
                                data-testid="contract-start-date"
                            />
                        </FieldRow>
                        <FieldRow label="End date">
                            <DateInput
                                value={form.end_date || ""}
                                onChange={(v) => set("end_date", v)}
                                data-testid="contract-end-date"
                            />
                        </FieldRow>
                        <FieldRow label="Salary type" required>
                            <SelectField
                                value={form.salary_type}
                                onChange={(v) => set("salary_type", v)}
                                items={
                                    isCasual
                                        ? [
                                              { value: "hourly", label: "Hourly" },
                                              { value: "daily", label: "Daily" },
                                          ]
                                        : [
                                              { value: "weekly", label: "Weekly" },
                                              { value: "bi_weekly", label: "Bi-Weekly" },
                                              { value: "monthly", label: "Monthly" },
                                          ]
                                }
                                testid="contract-salary-type"
                            />
                        </FieldRow>
                        <FieldRow label="Payment frequency" required>
                            <SelectField
                                value={form.payment_frequency}
                                onChange={(v) => set("payment_frequency", v)}
                                items={[
                                    { value: "daily", label: "Daily" },
                                    { value: "weekly", label: "Weekly" },
                                    { value: "bi_weekly", label: "Bi-Weekly" },
                                    { value: "monthly", label: "Monthly" },
                                ]}
                                testid="contract-pay-freq"
                            />
                        </FieldRow>
                        <FieldRow
                            label={isCasual ? "Rate (USD)" : "Basic salary (USD)"}
                            required
                        >
                            <Input
                                type="number"
                                min="0"
                                step="0.01"
                                required
                                value={form.basic_salary}
                                onChange={(e) => set("basic_salary", e.target.value)}
                                data-testid="contract-basic-salary"
                            />
                        </FieldRow>
                        {isSmart && (
                            <FieldRow label="Benefits component (USD)">
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={form.benefits_component}
                                    onChange={(e) =>
                                        set("benefits_component", e.target.value)
                                    }
                                    data-testid="contract-benefits"
                                />
                            </FieldRow>
                        )}
                        <FieldRow label="Payment method" required>
                            <SelectField
                                value={form.payment_method}
                                onChange={(v) => set("payment_method", v)}
                                items={[
                                    { value: "bank_transfer", label: "Bank Transfer" },
                                    { value: "mobile_money", label: "Mobile Money" },
                                    { value: "cash", label: "Cash" },
                                ]}
                                testid="contract-pay-method"
                            />
                        </FieldRow>
                    </div>

                    {isCasual && (
                        <FieldRow label="Task / assignment">
                            <Textarea
                                rows={2}
                                value={form.task_or_assignment}
                                onChange={(e) =>
                                    set("task_or_assignment", e.target.value)
                                }
                                data-testid="contract-task"
                            />
                        </FieldRow>
                    )}

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="contract-form-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="asl-navy-bg text-white"
                            disabled={submitting}
                            data-testid="contract-form-submit"
                        >
                            {submitting ? "Saving…" : "Create contract"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function FieldRow({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-[color:var(--asl-muted)]">
                {label} {required && <span className="asl-crimson-text">*</span>}
            </Label>
            {children}
        </div>
    );
}

function SelectField({ value, onChange, items, testid }) {
    return (
        <Select value={value || undefined} onValueChange={onChange}>
            <SelectTrigger data-testid={testid}>
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {items.map((i) => (
                    <SelectItem key={i.value} value={i.value} data-testid={`${testid}-opt-${i.value}`}>
                        {i.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
